import React, { useEffect, useState } from 'react';
import {
  View,
  ScrollView,
  StyleSheet,
  RefreshControl,
  Dimensions,
} from 'react-native';
import {
  Text,
  ProgressBar,
  Surface,
  useTheme,
  ActivityIndicator,
  Divider,
  Button,
} from 'react-native-paper';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation, useRoute } from '@react-navigation/native';
import {
  Award,
  Clock,
  Flame,
  CheckCircle,
  BarChart3,
  Trophy,
  Star,
} from 'lucide-react-native';
import { useAppDispatch, useAppSelector } from '../../store';
import {
  fetchJourney,
  fetchJourneyStats,
} from '../../store/slices/journeySlice';
import type { Journey, JourneyStats } from '@coffeeplexity/shared';

const { width } = Dimensions.get('window');

interface RouteParams {
  journeyId: string;
}

export function ProgressTrackerScreen() {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const navigation = useNavigation();
  const route = useRoute();
  const dispatch = useAppDispatch();

  const { journeyId } = route.params as RouteParams;
  const { currentJourney, journeyStats, loading, error } = useAppSelector(
    state => state.journey
  );
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    dispatch(fetchJourney(journeyId));
    dispatch(fetchJourneyStats(journeyId));
  }, [dispatch, journeyId]);

  const onRefresh = async () => {
    setRefreshing(true);
    await Promise.all([
      dispatch(fetchJourney(journeyId)),
      dispatch(fetchJourneyStats(journeyId)),
    ]);
    setRefreshing(false);
  };

  if (loading.journey && !currentJourney) {
    return (
      <View
        style={[
          styles.container,
          styles.centered,
          { backgroundColor: theme.colors.background },
        ]}
      >
        <ActivityIndicator size='large' />
        <Text style={styles.loadingText}>Loading progress...</Text>
      </View>
    );
  }

  if (!currentJourney) {
    return (
      <View
        style={[
          styles.container,
          styles.centered,
          { backgroundColor: theme.colors.background },
        ]}
      >
        <Text variant='headlineSmall'>Journey not found</Text>
      </View>
    );
  }

  const completedWeeks = currentJourney.weeks.filter(
    w => w.is_completed
  ).length;
  const totalActivities = currentJourney.weeks.reduce(
    (sum: number, w: any) => sum + w.activities.length,
    0
  );
  const completedActivities = currentJourney.weeks.reduce(
    (sum: number, w: any) =>
      sum + w.activities.filter((a: any) => a.is_completed).length,
    0
  );

  return (
    <ScrollView
      style={[
        styles.container,
        { paddingTop: insets.top, backgroundColor: theme.colors.background },
      ]}
      contentContainerStyle={styles.contentContainer}
      refreshControl={
        <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
      }
    >
      {/* Header */}
      <View style={styles.header}>
        <Text variant='headlineMedium' style={styles.title}>
          Progress Tracker
        </Text>
        <Text variant='bodyMedium' style={styles.subtitle}>
          {currentJourney.title}
        </Text>
      </View>

      {/* Overall Progress */}
      <Surface style={styles.overallProgressCard} elevation={1}>
        <View style={styles.progressHeader}>
          <Text variant='titleLarge' style={styles.progressTitle}>
            Overall Progress
          </Text>
          <Text variant='titleLarge' style={styles.progressPercentage}>
            {Math.round(currentJourney.progress_percentage)}%
          </Text>
        </View>
        <ProgressBar
          progress={currentJourney.progress_percentage / 100}
          color={theme.colors.primary}
          style={styles.mainProgressBar}
        />
        <View style={styles.progressStats}>
          <View style={styles.progressStat}>
            <Text variant='bodySmall' style={styles.statLabel}>
              Week {currentJourney.current_week} of{' '}
              {currentJourney.duration_weeks}
            </Text>
          </View>
          <View style={styles.progressStat}>
            <Text variant='bodySmall' style={styles.statLabel}>
              {completedActivities} of {totalActivities} activities
            </Text>
          </View>
        </View>
      </Surface>

      {/* Key Metrics */}
      <View style={styles.metricsGrid}>
        <Surface style={styles.metricCard} elevation={1}>
          <View style={styles.metricIcon}>
            <Flame size={24} color={theme.colors.error} />
          </View>
          <Text variant='titleMedium' style={styles.metricValue}>
            {journeyStats?.current_streak || 0}
          </Text>
          <Text variant='bodySmall' style={styles.metricLabel}>
            Current Streak
          </Text>
        </Surface>

        <Surface style={styles.metricCard} elevation={1}>
          <View style={styles.metricIcon}>
            <Trophy size={24} color={theme.colors.tertiary} />
          </View>
          <Text variant='titleMedium' style={styles.metricValue}>
            {journeyStats?.longest_streak || 0}
          </Text>
          <Text variant='bodySmall' style={styles.metricLabel}>
            Best Streak
          </Text>
        </Surface>

        <Surface style={styles.metricCard} elevation={1}>
          <View style={styles.metricIcon}>
            <Clock size={24} color={theme.colors.secondary} />
          </View>
          <Text variant='titleMedium' style={styles.metricValue}>
            {Math.round((journeyStats?.total_time_spent_minutes || 0) / 60)}h
          </Text>
          <Text variant='bodySmall' style={styles.metricLabel}>
            Time Spent
          </Text>
        </Surface>

        <Surface style={styles.metricCard} elevation={1}>
          <View style={styles.metricIcon}>
            <Award size={24} color={theme.colors.primary} />
          </View>
          <Text variant='titleMedium' style={styles.metricValue}>
            {journeyStats?.skills_mastered || 0}
          </Text>
          <Text variant='bodySmall' style={styles.metricLabel}>
            Skills Mastered
          </Text>
        </Surface>
      </View>

      {/* Weekly Progress */}
      <Surface style={styles.weeklyProgressCard} elevation={1}>
        <Text variant='titleMedium' style={styles.sectionTitle}>
          Weekly Progress
        </Text>
        <View style={styles.weeksList}>
          {currentJourney.weeks.map((week: any) => {
            const weekCompletedActivities = week.activities.filter(
              (a: any) => a.is_completed
            ).length;
            const weekTotalActivities = week.activities.length;
            const weekProgress =
              weekTotalActivities > 0
                ? (weekCompletedActivities / weekTotalActivities) * 100
                : 0;

            return (
              <View key={week.id} style={styles.weekItem}>
                <View style={styles.weekHeader}>
                  <View style={styles.weekInfo}>
                    <Text variant='titleSmall' style={styles.weekTitle}>
                      Week {week.week_number}
                    </Text>
                    <Text variant='bodySmall' style={styles.weekSubtitle}>
                      {week.title}
                    </Text>
                  </View>
                  <View style={styles.weekStatus}>
                    {week.is_completed ? (
                      <CheckCircle size={20} color={theme.colors.primary} />
                    ) : week.is_unlocked ? (
                      <Text variant='bodySmall' style={styles.weekProgress}>
                        {Math.round(weekProgress)}%
                      </Text>
                    ) : (
                      <Text variant='bodySmall' style={styles.lockedText}>
                        Locked
                      </Text>
                    )}
                  </View>
                </View>
                {week.is_unlocked && !week.is_completed && (
                  <ProgressBar
                    progress={weekProgress / 100}
                    color={theme.colors.primary}
                    style={styles.weekProgressBar}
                  />
                )}
                <View style={styles.weekMeta}>
                  <Text variant='bodySmall' style={styles.weekMetaText}>
                    {weekCompletedActivities}/{weekTotalActivities} activities
                  </Text>
                  <Text variant='bodySmall' style={styles.weekMetaText}>
                    ~{week.estimated_hours}h estimated
                  </Text>
                </View>
              </View>
            );
          })}
        </View>
      </Surface>

      {/* Activity Types Breakdown */}
      <Surface style={styles.breakdownCard} elevation={1}>
        <Text variant='titleMedium' style={styles.sectionTitle}>
          Activity Types
        </Text>
        <View style={styles.activityTypes}>
          {getActivityTypeBreakdown(currentJourney).map(type => (
            <View key={type.type} style={styles.activityTypeItem}>
              <View style={styles.activityTypeHeader}>
                <Text variant='bodyMedium' style={styles.activityTypeName}>
                  {type.type}
                </Text>
                <Text variant='bodySmall' style={styles.activityTypeCount}>
                  {type.completed}/{type.total}
                </Text>
              </View>
              <ProgressBar
                progress={type.total > 0 ? type.completed / type.total : 0}
                color={getActivityTypeColor(type.type, theme)}
                style={styles.activityTypeProgress}
              />
            </View>
          ))}
        </View>
      </Surface>

      {/* Learning Objectives Progress */}
      <Surface style={styles.objectivesCard} elevation={1}>
        <Text variant='titleMedium' style={styles.sectionTitle}>
          Learning Objectives
        </Text>
        <View style={styles.objectivesList}>
          {currentJourney.weeks
            .filter((w: any) => w.is_unlocked)
            .flatMap((w: any) => w.learning_objectives)
            .slice(0, 5) // Show first 5 objectives
            .map((objective: string, index: number) => (
              <View key={index} style={styles.objectiveItem}>
                <CheckCircle
                  size={16}
                  color={theme.colors.primary}
                  style={styles.objectiveIcon}
                />
                <Text variant='bodyMedium' style={styles.objectiveText}>
                  {objective}
                </Text>
              </View>
            ))}
        </View>
      </Surface>

      {/* Time Analysis */}
      {journeyStats && (
        <Surface style={styles.timeAnalysisCard} elevation={1}>
          <Text variant='titleMedium' style={styles.sectionTitle}>
            Time Analysis
          </Text>
          <View style={styles.timeStats}>
            <View style={styles.timeStat}>
              <BarChart3 size={20} color={theme.colors.secondary} />
              <View style={styles.timeStatInfo}>
                <Text variant='bodyMedium' style={styles.timeStatValue}>
                  {Math.round(journeyStats.average_session_minutes)} min
                </Text>
                <Text variant='bodySmall' style={styles.timeStatLabel}>
                  Avg Session
                </Text>
              </View>
            </View>
            <Divider style={styles.timeDivider} />
            <View style={styles.timeStat}>
              <Clock size={20} color={theme.colors.tertiary} />
              <View style={styles.timeStatInfo}>
                <Text variant='bodyMedium' style={styles.timeStatValue}>
                  {Math.round(journeyStats.total_time_spent_minutes / 60)}h{' '}
                  {journeyStats.total_time_spent_minutes % 60}m
                </Text>
                <Text variant='bodySmall' style={styles.timeStatLabel}>
                  Total Time
                </Text>
              </View>
            </View>
          </View>
        </Surface>
      )}

      {/* Achievements */}
      {journeyStats && journeyStats.achievements_earned > 0 && (
        <Surface style={styles.achievementsCard} elevation={1}>
          <Text variant='titleMedium' style={styles.sectionTitle}>
            Achievements
          </Text>
          <View style={styles.achievementsList}>
            <View style={styles.achievementItem}>
              <Star size={24} color={theme.colors.primary} />
              <View style={styles.achievementInfo}>
                <Text variant='bodyMedium' style={styles.achievementTitle}>
                  {journeyStats.achievements_earned} Achievement
                  {journeyStats.achievements_earned !== 1 ? 's' : ''} Earned
                </Text>
                <Text variant='bodySmall' style={styles.achievementDescription}>
                  Keep up the great work!
                </Text>
              </View>
            </View>
          </View>
        </Surface>
      )}
    </ScrollView>
  );
}

// Helper functions
function getActivityTypeBreakdown(journey: Journey) {
  const breakdown: Record<string, { completed: number; total: number }> = {};

  journey.weeks.forEach((week: any) => {
    week.activities.forEach((activity: any) => {
      const type = activity.activity_type;
      if (!breakdown[type]) {
        breakdown[type] = { completed: 0, total: 0 };
      }
      breakdown[type].total++;
      if (activity.is_completed) {
        breakdown[type].completed++;
      }
    });
  });

  return Object.entries(breakdown).map(([type, data]) => ({
    type: type.charAt(0).toUpperCase() + type.slice(1),
    ...data,
  }));
}

function getActivityTypeColor(type: string, theme: any) {
  switch (type.toLowerCase()) {
    case 'video':
      return theme.colors.error;
    case 'recipe':
      return theme.colors.primary;
    case 'reading':
      return theme.colors.tertiary;
    case 'quiz':
      return theme.colors.secondary;
    case 'practice':
      return theme.colors.outline;
    default:
      return theme.colors.onSurfaceVariant;
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  contentContainer: {
    padding: 16,
    paddingBottom: 32,
  },
  backButton: {
    alignSelf: 'flex-start',
  },
  centered: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 16,
  },
  header: {
    marginBottom: 24,
  },
  title: {
    fontWeight: 'bold',
    marginBottom: 4,
  },
  subtitle: {
    opacity: 0.7,
  },
  overallProgressCard: {
    padding: 20,
    marginBottom: 20,
    borderRadius: 12,
  },
  progressHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  progressTitle: {
    fontWeight: 'bold',
  },
  progressPercentage: {
    fontWeight: 'bold',
  },
  mainProgressBar: {
    height: 12,
    borderRadius: 6,
    marginBottom: 12,
  },
  progressStats: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  progressStat: {
    flex: 1,
  },
  statLabel: {
    opacity: 0.7,
  },
  metricsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 20,
  },
  metricCard: {
    flex: 1,
    minWidth: (width - 44) / 2, // Account for padding and gap
    padding: 16,
    borderRadius: 12,
    alignItems: 'center',
  },
  metricIcon: {
    marginBottom: 8,
  },
  metricValue: {
    fontWeight: 'bold',
    marginBottom: 4,
  },
  metricLabel: {
    opacity: 0.7,
    textAlign: 'center',
  },
  weeklyProgressCard: {
    padding: 16,
    marginBottom: 16,
    borderRadius: 12,
  },
  sectionTitle: {
    fontWeight: 'bold',
    marginBottom: 16,
  },
  weeksList: {
    gap: 16,
  },
  weekItem: {
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.1)',
  },
  weekHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  weekInfo: {
    flex: 1,
  },
  weekTitle: {
    fontWeight: 'bold',
    marginBottom: 2,
  },
  weekSubtitle: {
    opacity: 0.7,
  },
  weekStatus: {
    alignItems: 'flex-end',
  },
  weekProgress: {
    fontWeight: 'bold',
  },
  lockedText: {
    opacity: 0.5,
  },
  weekProgressBar: {
    height: 6,
    borderRadius: 3,
    marginBottom: 8,
  },
  weekMeta: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  weekMetaText: {
    opacity: 0.7,
  },
  breakdownCard: {
    padding: 16,
    marginBottom: 16,
    borderRadius: 12,
  },
  activityTypes: {
    gap: 12,
  },
  activityTypeItem: {
    marginBottom: 8,
  },
  activityTypeHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6,
  },
  activityTypeName: {
    fontWeight: '500',
  },
  activityTypeCount: {
    opacity: 0.7,
  },
  activityTypeProgress: {
    height: 6,
    borderRadius: 3,
  },
  objectivesCard: {
    padding: 16,
    marginBottom: 16,
    borderRadius: 12,
  },
  objectivesList: {
    gap: 12,
  },
  objectiveItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  objectiveIcon: {
    marginRight: 8,
    marginTop: 2,
  },
  objectiveText: {
    flex: 1,
    lineHeight: 20,
  },
  timeAnalysisCard: {
    padding: 16,
    marginBottom: 16,
    borderRadius: 12,
  },
  timeStats: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  timeStat: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  timeStatInfo: {
    marginLeft: 8,
  },
  timeStatValue: {
    fontWeight: 'bold',
  },
  timeStatLabel: {
    opacity: 0.7,
  },
  timeDivider: {
    width: 1,
    height: 40,
    marginHorizontal: 16,
  },
  achievementsCard: {
    padding: 16,
    borderRadius: 12,
  },
  achievementsList: {
    gap: 12,
  },
  achievementItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  achievementInfo: {
    marginLeft: 12,
    flex: 1,
  },
  achievementTitle: {
    fontWeight: 'bold',
    marginBottom: 2,
  },
  achievementDescription: {
    opacity: 0.7,
  },
});
